import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Table, Tabs, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

import {
  useInvestigationsServiceGetTbScreeningListEndpoint,
  useInvestigationsServiceGetTbScreeningListEndpointKey,
  useInvestigationsServiceTransmitTbCaseEndpoint
} from 'src/api/queries'
import { ApiError, TbScreeningPersonItem } from 'src/api/requests'
import { notification, PageBreadcrumbs, SaveConfirm } from 'src/components'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { TbCaseReadback } from '../components/TbCaseReadback'

type StatusTab = 'all' | 'transmitted' | 'not_transmitted'

export const ScreeningTbPage = () => {
  const { t } = useLocales()

  const [activeTab, setActiveTab] = useState<StatusTab>('all')
  const pagination = useTablePagination()

  const { data, isLoading } =
    useInvestigationsServiceGetTbScreeningListEndpoint({
      pageNumber: Number(pagination.current),
      pageSize: Number(pagination.pageSize)
    })

  // The endpoint exposes only pageNumber/pageSize (no status filter yet), so the
  // case-status submenu filters the currently loaded page client-side.
  const rows = useMemo(() => {
    const items = data?.data ?? []

    if (activeTab === 'transmitted') {
      return items.filter((item) => item.transmitted)
    }

    if (activeTab === 'not_transmitted') {
      return items.filter((item) => !item.transmitted)
    }

    return items
  }, [data?.data, activeTab])

  const queryClient = useQueryClient()

  const {
    mutate: transmitTbCase,
    isPending: isTransmitting,
    variables: transmittingCase
  } = useInvestigationsServiceTransmitTbCaseEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body?.errors),
    onSuccess: () => {
      notification.success({ message: t('persons.notify_tb_new_case_success') })
      void queryClient.invalidateQueries({
        queryKey: [useInvestigationsServiceGetTbScreeningListEndpointKey]
      })
    }
  })

  const columns: ColumnsType<TbScreeningPersonItem> = [
    {
      key: 'lastName',
      title: t('form.last_name'),
      dataIndex: 'lastName'
    },
    {
      key: 'firstName',
      title: t('form.first_name'),
      dataIndex: 'firstName'
    },
    {
      key: 'idnp',
      title: t('form.idnp'),
      render: (_, { idnp }) => idnp ?? '-'
    },
    {
      key: 'simeCode',
      title: t('screening_tb.sime_code'),
      render: (_, { simeCode }) => simeCode ?? '-'
    },
    {
      key: 'birthDate',
      title: t('form.birth_date'),
      render: (_, { birthDate }) =>
        birthDate ? dayjs(birthDate).format(dayMonthYearFormatWithDot) : '-'
    },
    {
      key: 'transmitted',
      title: t('screening_tb.status'),
      render: (_, { transmitted }) =>
        transmitted ? (
          <Tag color="green">{t('screening_tb.transmitted')}</Tag>
        ) : (
          <Tag>{t('screening_tb.not_transmitted')}</Tag>
        )
    },
    {
      key: 'actions',
      title: t('common.action'),
      align: 'center',
      render: (_, { id, transmitted }) =>
        transmitted ? null : (
          <SaveConfirm
            title={`${t('persons.notify_tb_new_case')}?`}
            onConfirm={() => transmitTbCase({ personId: String(id) })}
          >
            <Button
              type="link"
              loading={isTransmitting && transmittingCase?.personId === id}
            >
              {t('persons.notify_tb_new_case')}
            </Button>
          </SaveConfirm>
        )
    }
  ]

  const tabItems = [
    { key: 'all', label: t('screening_tb.tab_all') },
    { key: 'transmitted', label: t('screening_tb.tab_transmitted') },
    { key: 'not_transmitted', label: t('screening_tb.tab_not_transmitted') }
  ]

  return (
    <PageBreadcrumbs
      title={t('screening_tb.title')}
      items={[{ title: t('screening_tb.title') }]}
    >
      <Flex vertical gap={16}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as StatusTab)}
          items={tabItems}
        />

        <Table
          bordered
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
          expandable={{
            rowExpandable: (record) =>
              !!record.transmitted && !!record.simeTbExternalId,
            expandedRowRender: (record) =>
              record.simeTbExternalId ? (
                <TbCaseReadback simeTbExternalId={record.simeTbExternalId} />
              ) : null
          }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
